import * as PDFObject from 'pdfobject';
import PropTypes from 'prop-types';
import fetchFileMetadata from './actions';
import Loader from '../../../global/Components/Loader';

const getDefaultOptions = () =>
	window.__epdf__ || {
		forcePDFJS: true,
		PDFJS_URL: `${window.appContext}/res/edocatpdf/index.html`,
		PDFJS_VERSION: '3.11.107', //should be set automatically..
	};

export default class Preview extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			error: false,
			initiated: false,
			mimetype: null,
			size: null,
		};
	}

	static defaultProps = {
		active: true,
		id: 'documentPreview',
		index: 0,
		options: {},
	};

	static propTypes = {
		active: PropTypes.bool,
		fileName: PropTypes.string,
		fetchFile: PropTypes.func,
		id: PropTypes.string,
		index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		className: PropTypes.string,
		mimetype: PropTypes.string,
		nodeRef: PropTypes.string,
		objectURL: PropTypes.string,
		options: PropTypes.object,
	};

	static imageExclude = ['image/tiff', 'image/vnd.dwg'];

	componentDidMount() {
		this.getFile();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.nodeRef !== this.props.nodeRef) {
			this.setState(
				() => ({
					initiated: false,
					mimetype: null,
					error: false,
					size: 0,
				}),
				() => {
					this.getFile();
				}
			);
		}

		this.initPreview();
	}

	getRole = async () => {
		const { nodeRef } = this.props;

		if (nodeRef) {
			const node = await fetch(
				`${window.appContext}/proxy/alfresco/slingshot/doclib2/node/${nodeRef.replace(':/', '')}`
			)
				.then(response => {
					if (response.status !== 200) {
						throw new Error(response);
					}
					return response.json();
				})
				.catch(reason => {
					throw reason;
				});

			const roles = node?.item?.node?.permissions?.user;

			if (roles?.eReader && !roles?.Contributor) {
				return {
					pdfOpenParams: {
						edownload: false,
						eprint: false,
					},
				};
			} else {
				return;
			}
		} else {
			return;
		}
	};

	getFile = async () => {
		const { nodeRef, fetchFile, index, id, objectURL } = this.props;
		const role = await this.getRole();
		if (nodeRef && fetchFile) {
			fetchFileMetadata(nodeRef);
		} else if (nodeRef) {
			fetchFileMetadata(nodeRef)
				.then(response => {
					this.setState({
						mimetype: response.mimetype,
						size: response.size,
					});
				})
				.catch(() => {
					this.setState({
						error: true,
					});
				});
		} else if (objectURL) {
			const options = {
				...getDefaultOptions(),
				...this.props.options,
				...role,
			};

			PDFObject.embed(objectURL, `#${id}-${index}`, options);
		}
	};

	initPreview = async () => {
		const defaultOptions = getDefaultOptions();
		const role = await this.getRole();
		const { mimetype: propsMimetype, active } = this.props;
		const { mimetype: stateMimetype, initiated } = this.state;
		const fileName = this.getFileName();
		const mimetype = stateMimetype || propsMimetype;

		if (!initiated && active && mimetype) {
			this.setState({
				initiated: true,
			});
			const { nodeRef, index, id } = this.props;
			const nodeRefPath = nodeRef.replace(':/', '');
			const options = {
				...defaultOptions,
				...this.props.options,
				...role,
			};

			if (mimetype === 'application/pdf' && defaultOptions.pdftopdf !== true) {
				const sp = new URLSearchParams({
					version: defaultOptions.PDFJS_VERSION,
					nocache: moment().unix(),
				})
				if (fileName) {
					sp.set('saveName', fileName)
				}
				const url = `${window.appContext}/proxy/alfresco/api/node/${nodeRefPath}/${fileName}/content;cm:content?${sp}`
				PDFObject.embed(url, `#${id}-${index}`, options);
			} else if (mimetype.includes('image/') && !Preview.imageExclude.includes(mimetype)) {
				// images are solved in render
			} else {
				const sp = new URLSearchParams({
					nodeRef: nodeRef,
					fileName: fileName,
					nocache: moment().unix(),
				})
				const url = `${window.appContext}/proxy/alfresco/edocat/NodePDF?${sp}`;
				fetch(url, { credentials: 'same-origin' })
					.then(response => {
						if (response.status !== 200) {
							this.setState({
								error: true,
							});
						}
						PDFObject.embed(url, `#${id}-${index}`, options);
					})
					.catch(err => {
						this.setState({
							error: true,
						});
						throw err;
					});
			}
		}
	};

	getFileName = () => {
		const { fileName } = this.props;
		if (!fileName) return 'Document with unknown name';
		return this.encodeFilename(fileName);
	};

	encodeFilename = name => {
		return name.endsWith('.pdf') ? encodeURIComponent(name) : `${encodeURIComponent(name)}.pdf`;
	};

	render() {
		const { active, id, index, nodeRef, className, mimetype: propsMimetype, filename } = this.props;
		const { error, mimetype: stateMimetype, size } = this.state;
		const mimetype = stateMimetype || propsMimetype;

		return (
			<div className="Edocat-Document-Preview">
				{typeof mimetype !== 'undefined' && !mimetype ? (
					<div className='status-message'>
						<Loader text={window.Alfresco.messages.global['docPreview.status.view_generating']} />
					</div>
				) : (
					<>
						{(error || size === 0) && (
							<div className='error-message'>
								<p>{window.Alfresco.messages.global['docPreview.errors.defaultText']}</p>
							</div>
						)}
						{!error && active && (
							<div
								id={`${id}-${index}`}
								className={className ? ' Document-Preview ' + className : ' Document-Preview'}
								key={index}
							>
								{mimetype && mimetype.includes('image/') && !Preview.imageExclude.includes(mimetype) ? (
									<img
										alt={filename}
										className="Preview-Image"
										src={`${window.appContext}/proxy/alfresco/api/node/${nodeRef.replace(
											':/',
											''
										)}/content;cm:content?${performance.now()}`}
									/>
								) : (
									<div className="status-message">
										<Loader text={window.Alfresco.messages.global['docPreview.status.view_generating']} />
									</div>
								)}
							</div>
						)}
					</>
				)}
			</div>
		);
	}
}
